import React, { useEffect, useState, useMemo } from "react";
import { APIProvider, Map, AdvancedMarker, Pin } from "@vis.gl/react-google-maps";
import { User } from "../../models/user.model";
import { UserDistricts } from "../../models/userdistricts.model";
import { responseHero } from "../../utils/axiosInterceptor";
import { toast } from "react-toastify";
import { fetchClusterUsers } from "../../api/fetchClusterUsers";
import { fetchUserDistricts } from "../../api/fetchUserDistricts";

type ClusterSelection = number | "all";

export default function UserMap() {
  const [usersWithCoords, setUsersWithCoords] = useState<User[]>([]);
  const [districts, setDistricts] = useState<UserDistricts[]>([]);
  const [selectedCluster, setSelectedCluster] = useState<ClusterSelection>("all");
  const [isSending, setIsSending] = useState(false);
  const [hoveredUserId, setHoveredUserId] = useState<string | number | null>(null);
  
  // New state for checkboxes
  const [selectedDays, setSelectedDays] = useState<string[]>(["Kedd", "Szerda", "Csütörtök"]);

  const days = ["Kedd", "Szerda", "Csütörtök"];

  const toggleDay = (day: string) => {
    setSelectedDays(prev => 
      prev.includes(day) ? prev.filter(d => d !== day) : [...prev, day]
    );
  };

  const getClusterColor = (clusterId: number | null | undefined) => {
    if (clusterId === null || clusterId === undefined) return "#6366f1";
    const hue = (clusterId * 137.5) % 360;
    return `hsl(${hue}, 65%, 55%)`;
  };

  useEffect(() => {
    const loadData = async () => {
      try {
        const [users, fetchedDistricts] = await Promise.all([
          fetchClusterUsers(),
          fetchUserDistricts()
        ]);
        setUsersWithCoords(users);
        setDistricts(fetchedDistricts);
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    };
    loadData();
  }, []);

  const validDistrictIds = useMemo(() => new Set(districts.map(d => d.id)), [districts]);

  const filteredUsers = useMemo(() => {
    const baseUsers = usersWithCoords.filter(u => u.clusterId !== null && validDistrictIds.has(u.clusterId as number));
    if (selectedCluster === "all") return baseUsers;
    return baseUsers.filter((u) => u.clusterId === selectedCluster);
  }, [selectedCluster, usersWithCoords, validDistrictIds]);

  const handleBulkAction = async () => {
    if (selectedCluster === "all") {
      toast.warn("Kérlek válassz ki egy konkrét körzetet!");
      return;
    }
    
    if (selectedDays.length === 0) {
      toast.warn("Kérlek válassz ki legalább egy napot!");
      return;
    }

    const districtName = districts.find(d => d.id === selectedCluster)?.name || selectedCluster;
    const confirmSend = window.confirm(
      `Biztosan ki akarod küldeni az értesítést (${selectedDays.join(", ")}) a(z) ${districtName} körzet ${filteredUsers.length} tagjának?`
    );

    if (!confirmSend) return;

    setIsSending(true);
    try {
      // Note: You can pass selectedDays to your API if needed
      await responseHero.post(`${process.env.REACT_APP_API_URL}/cluster/bulk/${selectedCluster}`, {
        days: selectedDays.join(',')
      });
      toast.success(`${districtName}: Értesítések elküldve.`);
    } catch (error) {
      toast.error("Hiba történt a küldés során.");
    } finally {
      setIsSending(false);
    }
  };

  return (
    <div className="p-8 font-sans bg-[#fbfbfd] min-h-screen text-[#1d1d1f]">
      <header className="mb-10">
        <h1 className="text-3xl font-semibold tracking-tight">Ügyféltérkép</h1>
        <p className="text-gray-500 mt-1 font-medium">Zónák kezelése és csoportos kommunikáció</p>
      </header>

      <div className="grid grid-cols-1 lg:grid-cols-[340px_1fr] gap-8 max-w-[1700px] mx-auto">
        <aside className="flex flex-col gap-6">
          <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-200">
            <label className="block mb-3 text-xs font-bold uppercase tracking-wider text-gray-400">
              Körzet Kiválasztása
            </label>
            <div className="relative group mb-6">
              <select
                value={selectedCluster}
                onChange={(e) => {
                  const val = e.target.value;
                  setSelectedCluster(val === "all" ? "all" : Number(val));
                }}
                className="w-full appearance-none bg-gray-50 border border-gray-200 p-3 rounded-xl focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 outline-none transition-all cursor-pointer text-sm font-medium pr-10"
              >
                <option value="all">Minden körzet ({usersWithCoords.length})</option>
                {districts.map(district => (
                  <option key={district.id} value={district.id}>
                    {district.name}
                  </option>
                ))}
              </select>
              <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none text-gray-400">
                <svg width="12" height="12" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M19 9l-7 7-7-7"></path></svg>
              </div>
            </div>

            {/* Checkbox Group */}
            <label className="block mb-3 text-xs font-bold uppercase tracking-wider text-gray-400">
              Érintett napok
            </label>
            <div className="flex flex-wrap gap-3 mb-6">
              {days.map((day) => (
                <label key={day} className="flex items-center gap-2 cursor-pointer group">
                  <div className="relative flex items-center">
                    <input
                      type="checkbox"
                      checked={selectedDays.includes(day)}
                      onChange={() => toggleDay(day)}
                      className="peer h-5 w-5 cursor-pointer appearance-none rounded-md border border-gray-300 checked:bg-black checked:border-black transition-all"
                    />
                    <svg className="absolute h-3.5 w-3.5 text-white opacity-0 peer-checked:opacity-100 top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 pointer-events-none" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="4"><path d="M5 13l4 4L19 7"></path></svg>
                  </div>
                  <span className="text-sm font-medium text-gray-600 group-hover:text-black transition-colors">{day}</span>
                </label>
              ))}
              <p className="text-sm font-bold text-gray-600 py-2">A napok mindíg a jövő hétre vonatkoznak.</p>
            </div>
            
            <button
              onClick={handleBulkAction}
              disabled={selectedCluster === "all" || isSending}
              className={`w-full p-3 rounded-xl font-semibold text-sm transition-all
                ${selectedCluster === "all"
                  ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                  : "bg-black text-white hover:bg-gray-800 active:scale-[0.98]"}`}
            >
              {isSending ? "Küldés..." : "Csoportos üzenet"}
            </button>
          </div>

          <div className="bg-white rounded-2xl shadow-sm border border-gray-200 flex flex-col h-[420px]">
            <div className="p-4 border-b border-gray-100">
              <h3 className="text-xs font-bold uppercase tracking-wider text-gray-400">Ügyfelek a zónában</h3>
            </div>
            <div className="overflow-y-auto custom-scrollbar">
              {filteredUsers.length > 0 ? (
                filteredUsers.map((user) => (
                  <div 
                    key={user.user_id} 
                    className="px-4 py-3 flex items-center gap-3 hover:bg-gray-50 transition-colors border-b border-gray-50 last:border-0"
                    onMouseEnter={() => setHoveredUserId(user.user_id)}
                    onMouseLeave={() => setHoveredUserId(null)}
                  >
                    <div
                      className="w-1.5 h-1.5 rounded-full"
                      style={{ backgroundColor: getClusterColor(user.clusterId) }}
                    />
                    <div className="min-w-0">
                      <div className="text-sm font-medium text-gray-900 truncate">{user.name}</div>
                      <div className="text-[11px] text-gray-400 truncate uppercase tracking-tight">{user.email}</div>
                    </div>
                  </div>
                ))
              ) : (
                <div className="p-10 text-center text-gray-400 text-sm italic">Nincs találat</div>
              )}
            </div>
          </div>
        </aside>

        <main>
          {process.env.REACT_APP_GOOGLE_API_KEY ? (
            <div className="h-[750px] w-full rounded-3xl overflow-hidden shadow-2xl shadow-gray-200 border border-gray-200 bg-white">
              <APIProvider apiKey={process.env.REACT_APP_GOOGLE_API_KEY}>
                <Map
                  defaultCenter={{ lat: 47.1625, lng: 19.5033 }}
                  defaultZoom={7.5}
                  disableDefaultUI={true}
                  mapId={"4316b0f2e50ee2cb27516d6e"}
                >
                  {filteredUsers.map((user) => {
                    const isHovered = hoveredUserId === user.user_id;
                    return (
                      <AdvancedMarker
                        key={user.user_id}
                        position={{ lat: Number(user.lat), lng: Number(user.lng) }}
                        zIndex={isHovered ? 1000 : 1}
                      >
                        <div className="relative group">
                          <Pin 
                            background={getClusterColor(user.clusterId)} 
                            glyphColor={"#fff"} 
                            borderColor={"rgba(255,255,255,0.3)"}
                            scale={isHovered ? 1.2 : 0.9}
                          />
                          {isHovered && (
                            <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-3 px-3 py-2 bg-white/90 backdrop-blur-md text-black text-xs font-bold rounded-lg shadow-xl border border-gray-100 whitespace-nowrap animate-in fade-in slide-in-from-bottom-1 duration-200">
                              {user.name}
                              <div className="absolute top-full left-1/2 -translate-x-1/2 border-[6px] border-transparent border-t-white/90" />
                            </div>
                          )}
                        </div>
                      </AdvancedMarker>
                    );
                  })}
                </Map>
              </APIProvider>
            </div>
          ) : (
            <div className="h-[750px] w-full flex items-center justify-center bg-gray-50 rounded-3xl border border-dashed border-gray-300 text-gray-400 font-medium">
              Google Maps API Key hiányzik
            </div>
          )}
        </main>
      </div>

      <style>{`
        .custom-scrollbar::-webkit-scrollbar { width: 4px; }
        .custom-scrollbar::-webkit-scrollbar-track { background: transparent; }
        .custom-scrollbar::-webkit-scrollbar-thumb { background: #e5e7eb; border-radius: 10px; }
        .custom-scrollbar::-webkit-scrollbar-thumb:hover { background: #d1d5db; }
      `}</style>
    </div>
  );
}